import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi } from '../evidenceApi';
import type { EvidenceItemDetail, EvidenceItemListItem } from '../evidenceTypes';

const ID = 'evidence-markings';

/**
 * An item's MARKING — its purpose, set by hand after receiving. The exhibit number was removed
 * (08 Oct 2026), as the two statuses were before it.
 *
 * <b>Admission status and evidence status were REMOVED</b> — product call, 08 Oct 2026, with their
 * vocabularies. <b>Nothing here moves custody</b>: where the item is and who holds it change only by
 * a movement.
 */
export function EvidenceMarkingsModal({
  item,
  onClose,
  onSaved,
}: {
  item: EvidenceItemListItem;
  onClose: () => void;
  onSaved: (saved: EvidenceItemDetail) => void;
}) {
  const [purpose, setPurpose] = useState(item.purpose ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(
        await evidenceItemsApi.mark(item.id, {
          purpose: purpose.trim() || null,
        }),
      );
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the markings.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Markings — ${item.inventoryNumber}`}
      size="lg"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
          setPurpose(`Sample purpose ${suffix} — synthetic marking created by Autogenerate.`);
        },
        disabled: saving,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Textarea
          id={`${ID}-purpose`}
          label="Purpose"
          description="What the item is offered to prove."
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('purpose')}
        />
      </div>
    </Modal>
  );
}
